import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { Link } from 'react-router';
import { motion, useMotionValue, useSpring } from 'motion/react';
import type { Variants } from 'motion/react';
import { Badge, popIn, useReducedMotion } from '@redpandev/ui';

/**
 * Carte d'un jeu du hub (spec 05 §4.1).
 * - Jouable : toute la carte est cliquable via un lien-calque (`absolute inset-0`)
 *   ancré sur un wrapper JAMAIS transformé. Contrainte clé : la cible de clic ne
 *   doit pas bouger pendant le press — si l'élément sous le curseur change entre
 *   pointerdown et pointerup (tilt/lift/scale), le navigateur ne délivre pas de
 *   `click` au lien et peut démarrer une sélection de texte ou un drag natif.
 *   Les transforms (lift, tilt 3D ≤ 6°, scale .98 au tap) ne s'appliquent qu'à
 *   l'<article> visuel ; les gestes sont détectés sur le wrapper statique et
 *   propagés par variants (`hover`/`tap`).
 * - Tilt souris uniquement, coupé si reduced-motion ; halo glow-panda au survol.
 * - Teaser « Bientôt » (`comingSoon`) : non cliquable, `aria-disabled`,
 *   opacité réduite mais toujours jolie.
 */
export type GameCardProps = {
  /** Nom du jeu (titre display). */
  title: string;
  description: string;
  /** Visuel de couverture — décoratif, rendu dans un wrapper `aria-hidden`. */
  cover: ReactNode;
  /** Pastille d'info (« 2 joueurs · temps réel », « Bientôt »…). */
  badge?: string;
  /** Route du jeu (ex : `/motus`). Requis pour une carte jouable. */
  to?: string;
  /** Libellé du CTA (défaut « Jouer ») — nom accessible : `«CTA» à «titre»`. */
  cta?: string;
  /** Carte teaser non cliquable. */
  comingSoon?: boolean;
};

const MAX_TILT_DEG = 6;
const TILT_SPRING = { stiffness: 260, damping: 26 };

/** Transforms de survol/tap de la carte visuelle, déclenchés depuis le wrapper. */
const cardGestures: Variants = {
  hover: { y: -6 },
  tap: { scale: 0.98 },
};

export function GameCard({
  title,
  description,
  cover,
  badge,
  to,
  cta = 'Jouer',
  comingSoon = false,
}: GameCardProps) {
  const reduced = useReducedMotion();
  const rawRotateX = useMotionValue(0);
  const rawRotateY = useMotionValue(0);
  const rotateX = useSpring(rawRotateX, TILT_SPRING);
  const rotateY = useSpring(rawRotateY, TILT_SPRING);
  const playable = Boolean(to) && !comingSoon;

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    // Tilt réservé au pointeur souris (desktop) et coupé si reduced-motion.
    if (!playable || reduced || event.pointerType !== 'mouse') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    rawRotateY.set(px * MAX_TILT_DEG * 2);
    rawRotateX.set(-py * MAX_TILT_DEG * 2);
  };

  const resetTilt = () => {
    rawRotateX.set(0);
    rawRotateY.set(0);
  };

  return (
    <motion.div
      variants={popIn}
      whileHover={playable && !reduced ? 'hover' : undefined}
      whileTap={playable ? 'tap' : undefined}
      onPointerMove={handlePointerMove}
      onPointerLeave={resetTilt}
      className="group relative h-full select-none"
    >
      <motion.article
        variants={cardGestures}
        aria-disabled={comingSoon || undefined}
        style={{ rotateX, rotateY, transformPerspective: 900 }}
        className={
          'flex h-full flex-col rounded-xl border border-ink-600/60 bg-surface p-5 shadow-card' +
          (playable
            ? ' transition-shadow duration-fast ease-fun group-hover:shadow-glow-panda group-focus-within:shadow-glow-panda'
            : '') +
          (comingSoon ? ' opacity-75' : '')
        }
      >
        <div aria-hidden="true" className="mb-4">
          {cover}
        </div>
        <h2 className="font-display text-2xl font-bold text-ink-100">{title}</h2>
        <p className="mt-1 text-sm leading-relaxed text-ink-400">{description}</p>
        <div className="mt-4 flex flex-1 items-end justify-between gap-3">
          {badge ? <Badge intent={comingSoon ? 'neutral' : 'brand'}>{badge}</Badge> : <span />}
          {playable && (
            // CTA visuel uniquement : l'élément interactif est le lien-calque
            // ci-dessous (sinon le bouton bougerait avec la carte au tap).
            <span
              aria-hidden="true"
              className={
                'inline-flex h-11 items-center justify-center rounded-full bg-panda-500 px-5 ' +
                'font-display font-semibold text-ink-950 ' +
                'transition-[filter] duration-fast ease-fun group-hover:brightness-110'
              }
            >
              {cta}
            </span>
          )}
        </div>
      </motion.article>
      {playable && to && (
        <Link
          to={to}
          aria-label={`${cta} à ${title}`}
          draggable={false}
          className="absolute inset-0 rounded-xl"
        />
      )}
    </motion.div>
  );
}
